import React from "react";
import Map, {
  Marker,
  MapConstant,
  MapControls,
  Popup,
  MarkerLayer,
} from "../../../../../lib/Map";
import {
  getDispatchUnassignedPickupMarker,
  getDispatchAssignedPickupMarker,
} from "../../../../mapComponents/mapHelper";
import { withTheme } from "@material-ui/core";
import { compose } from "recompose";
import { observer, inject } from "mobx-react";
import { TerminalMarker } from "../../../../mapComponents/Markers";
import { withAppConfig } from "../../../../../appConfigContext";
import { 
  isPickupOnMapActive,
  getPickupStopData,
} from "../../../../modules/Dispatch/RouteExecution/DriverMap/utils";
import PickupPopupInfo from "../../../../modules/Dispatch/RouteExecution/DriverMap/components/PickupPopupInfo";
import { getSpecialNeedsOptionsWithServiceLevel } from "../../../../modules/utils";
import { isPickupExistInGrid } from "./utils";
import MapHeader from "./components/MapHeader";
import { getPickupStatus, getPickupStatusTypeIcon } from "../../../../modules/Dispatch/RouteExecution/utils/pickupStatusTypeIcon";
import { getPickupCountForPickupMarkerBadge } from "../../../../modules/Dispatch/utils/utils";
import appConstants from "@appConstants";

@inject("DispatchStore")
@observer
class PickupMap extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isAutoZoomEnabled: true,
      selectedPickups: [],
      selectedPickup: null,
      // Drives MarkerLayer updates after satellite / style changes (see ShipmentsMap).
      styleName: null,
    };
    this.pickupSelectedOnMap = false;
    this.suppressNextMapClick = false;
  }
  componentDidMount() {
    this.props.glEventHub.on("pickupSelectedOnGridPopup", this.pickupSelected);
  }
  componentWillUnmount() {
    this.props.glEventHub.off("pickupSelectedOnGridPopup", this.pickupSelected);
  }

  setRouteAnimation = (value) => {
    this.setState({
      isAutoZoomEnabled: value,
    });
  };

  getMapStyleName = (styleName) => {
    if (this.state.styleName !== styleName) {
      this.setState({ styleName });
    }
  };

  pickupSelected = (selectedPickups) => {
    const isPickupInGrid = isPickupExistInGrid(
      selectedPickups,
      this.state.selectedPickup?.pickupId
    );
    this.setState({
      selectedPickups,
      ...(!isPickupInGrid && { selectedPickup: null }),
    });
  };

  onMapClicked = () => {
    if (this.suppressNextMapClick) {
      this.suppressNextMapClick = false;
      return;
    }
    if (this.state.selectedPickup) {
      this.setState({
        selectedPickup: null,
      });
    }
    this.pickupSelectedOnMap = false;
  };

  onPickupMarkerClicked = (selectedPickup) => {
    this.suppressNextMapClick = true;
    let selectedPickups = [];
    const isActivePickup = isPickupOnMapActive(
      this.state.selectedPickups,
      selectedPickup.pickupId
    )
    if (!isActivePickup) {
      selectedPickups = [...this.state.selectedPickups, selectedPickup];
      this.pickupSelectedOnMap = true;
    } else {
      selectedPickups = this.state.selectedPickups.filter(
        (pickup) => pickup.pickupId !== selectedPickup.pickupId
      );
      this.pickupSelectedOnMap = false;
    }
    this.setState({ 
      selectedPickups,
      selectedPickup,
    });
    this.props.glEventHub.emit("pickupSelectedOnMapPopup", selectedPickups);
  };

  getMarkerLayerPickups = () => {
    return (this.props.pickupList || []).map((pickup) => ({
      ...pickup,
      latitude: pickup.shipperLatitude,
      longitude: pickup.shipperLongitude,
    }));
  };

  getPickupIcon = (pickup) => {
    const { theme, DispatchStore } = this.props;
    const isPickupActive = isPickupOnMapActive(
      this.state.selectedPickups,
      pickup.pickupId
    );
    let pickupStop = null;
    if (pickup.driverId) {
      pickupStop = getPickupStopData(DispatchStore.routesList, pickup);
    }
    return pickup.driverId
      ? getDispatchAssignedPickupMarker({
          pickup,
          isPickupActive,
          stopSequence: pickupStop?.stopData?.stopSequence,
          color: pickupStop?.stopColor,
          stopData: pickupStop?.stopData,
          theme,
        })
      : getDispatchUnassignedPickupMarker({
          isActive: isPickupActive,
          theme,
          pickupCount: getPickupCountForPickupMarkerBadge(pickup),
          pickupStatus: getPickupStatus({
            pickup,
            terminalOffset: DispatchStore.terminalOffset,
            newPickupDuration: this.props.configJSON?.newPickupDuration,
          }),
        });
  };

  render() {
    const {
      theme,
      userData: { userProfile },
      DispatchStore,
    } = this.props;
    const {selectedPickup, isAutoZoomEnabled} = this.state;
    const markerLayerPickups = this.getMarkerLayerPickups();
    const { location } = userProfile.terminal;
    let routesData;
    if (selectedPickup?.driverId) {
      routesData = DispatchStore.getCurrentDriver(
        selectedPickup?.driverId
      );
    }

    return (
      <>
        <MapHeader label="Pickups Map" setRouteAnimation={this.setRouteAnimation}/>
        <Map
          zoom={9}
          initialCenter={location}
          center={location}
          mapId={appConstants.mapIds.PICKUPS_MAP}
          mapTheme={theme.themeName}
          eventHub={this.props.glEventHub}
          mapConfig={this.props.configJSON?.maps}
          onClick={this.onMapClicked}
          getMapStyleName={this.getMapStyleName}
        >
          <MapControls
            controlOptions={{
              zoomControl: {
                position: MapConstant.CONTROL_POSITION.BOTTOM_RIGHT,
              },
              satelliteViewControl: {
                position: MapConstant.CONTROL_POSITION.BOTTOM_RIGHT,
              },
              reCenterMapControl: {
                position: MapConstant.CONTROL_POSITION.BOTTOM_RIGHT,
              },
            }}
          />
          <Marker
            position={location}
            animation
            zIndex={2}
            forceUpdate
            isActiveShipment
          >
            <TerminalMarker
              data={userProfile.terminal}
              color={theme.terminalContentColor}
            />
          </Marker>
          <MarkerLayer
            refreshOnMapStyleLoaded
            detachMarkerLayerListenersBeforeAttach
            code={userProfile.terminal.code}
            data={markerLayerPickups}
            dataKey="pickupId"
            activeMarkerIds={this.state.selectedPickups.map((pickup) =>
              pickup.pickupId?.toLowerCase()
            )}
            getIcon={this.getPickupIcon}
            onClick={this.onPickupMarkerClicked}
            thresholdZoomLevel={11}
            center={isAutoZoomEnabled ? location : null}
            styleName={this.state.styleName}
          />
          {selectedPickup && this.pickupSelectedOnMap && (
            <Popup
              position={{
                lat: selectedPickup.latitude || selectedPickup.shipperLatitude,
                lng: selectedPickup.longitude || selectedPickup.shipperLongitude,
              }}
              key={`${selectedPickup.pickupId}_pickupPopup`}>
              <PickupPopupInfo
                specialNeeds={getSpecialNeedsOptionsWithServiceLevel(
                  DispatchStore.priorityAllShipmentList
                )}
                routesData={routesData}
                selectedPickup={selectedPickup}
                terminal={userProfile.terminal}
                routesList={DispatchStore.routesList}
                currentActiveRoutes={DispatchStore.currentActiveRoutes}
                marker={getPickupStatusTypeIcon({
                  theme,
                  status: getPickupStatus({
                    pickup: selectedPickup,
                    terminalOffset: this.props.DispatchStore.terminalOffset,
                    newPickupDuration: this.props.configJSON?.newPickupDuration
                  })
                })}
                glEventHub={this.props.glEventHub}
                showPickupHistory={false}
              />
            </Popup>
          )}
        </Map>
      </>
    );
  }
}

export default compose(
  withTheme(),
  withAppConfig,
)(PickupMap);
